"use client";

import { Check } from "lucide-react";
import { Badge } from "@stockline/ui/components/badge";
import { useTranslation } from "react-i18next";
import type { ScopeOption } from "./types";

export function OptionButton({
  option,
  selected,
  onClick,
}: {
  readonly option: ScopeOption;
  readonly selected: boolean;
  readonly onClick: () => void;
}) {
  const { t } = useTranslation();

  let borderStyle = "border-border hover:border-muted-foreground/50";
  if (selected) {
    borderStyle = "border-primary bg-primary/5 ring-1 ring-primary";
  } else if (option.recommended) {
    borderStyle =
      "border-violet-300 bg-violet-50/50 dark:border-violet-700 dark:bg-violet-900/10";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left text-sm transition-all ${borderStyle}`}
      data-testid={`scope-option-${option.value}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="font-medium">{option.label}</span>
        {option.recommended && !selected && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 border-violet-300 text-violet-600 dark:border-violet-700 dark:text-violet-400"
          >
            {t("ai.userInput.recommended", "Recommended")}
          </Badge>
        )}
        {selected && <Check className="h-3.5 w-3.5 text-primary" />}
      </div>
      {option.description && (
        <span className="text-xs text-muted-foreground">
          {option.description}
        </span>
      )}
    </button>
  );
}
